import { Users, MapPin, Activity, Clock, Battery } from 'lucide-react'
import { FadeIn } from '../components/ui/FadeIn'
import { GlassCard } from '../components/ui/GlassCard'
import { SecurityMap } from '../components/landing/SecurityMap'
import { AnimatedNumber } from '../components/landing/AnimatedNumber'
import { useLanguage } from '../contexts/LanguageContext'

export function SecurityManagement() {
  const { t } = useLanguage()

  const stats = [
    {
      labelKey: 'security.guards.active',
      value: 24,
      icon: Users,
    },
    {
      labelKey: 'security.guards.patrol',
      value: 18,
      icon: Activity,
    },
    {
      labelKey: 'security.checkpoints',
      value: 156,
      icon: MapPin,
    },
    {
      labelKey: 'security.response',
      value: 2.3,
      suffix: ` ${t('security.time.min')}`,
      icon: Clock,
    },
  ]

  const recentActivity = [
    { guardKey: 'security.guard.beta', actionKey: 'security.action.patrol', time: 5, status: 'info' },
    { guardKey: 'security.guard.gamma', actionKey: 'security.action.incident', time: 8, status: 'warning' },
    { guardKey: 'security.guard.delta', actionKey: 'security.action.checkpoint', time: 12, status: 'success' },
  ]

  return (
    <section id="gestion" className="relative">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="max-w-2xl">
            <div className="text-xs font-semibold tracking-[0.2em] text-nexus-blue/80">
              {t('security.label')}
            </div>
            <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              {t('security.title')}
            </h2>
            <p className="mt-3 text-pretty text-white/70">
              {t('security.description')}
            </p>
          </div>
        </FadeIn>

        {/* Stats Grid */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <FadeIn key={stat.labelKey} delay={0.05 * index} className="h-full">
                <GlassCard className="h-full">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs text-white/60">{t(stat.labelKey)}</div>
                      <div className="mt-2 text-3xl font-semibold tracking-tight">
                        <AnimatedNumber
                          value={stat.value}
                          decimals={stat.suffix ? 1 : 0}
                        />
                        {stat.suffix && <span className="text-white/65">{stat.suffix}</span>}
                      </div>
                    </div>
                    <div className="grid size-11 place-items-center rounded-2xl bg-white/10 border border-white/10">
                      <Icon className="size-5 text-nexus-cyan" />
                    </div>
                  </div>
                </GlassCard>
              </FadeIn>
            )
          })}
        </div>

        {/* Map and Activity */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Map - Takes 2 columns */}
          <div className="lg:col-span-2">
            <SecurityMap />
          </div>

          {/* Recent Activity - Takes 1 column */}
          <div className="space-y-6">
            <FadeIn delay={0.2}>
              <GlassCard>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="text-sm font-semibold tracking-tight">{t('security.activity.title')}</div>
                    <div className="mt-1 text-xs text-white/65">
                      {t('security.activity.desc')}
                    </div>
                  </div>
                  <div className="grid size-10 place-items-center rounded-xl bg-white/10 border border-white/10">
                    <Activity className="size-5 text-nexus-cyan" />
                  </div>
                </div>
                <div className="space-y-2">
                  {recentActivity.map((activity, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-white/10 bg-white/6 px-3 py-2.5"
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                            activity.status === 'success'
                              ? 'bg-green-500'
                              : activity.status === 'info'
                                ? 'bg-blue-500'
                                : activity.status === 'warning'
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white">{t(activity.guardKey)}</p>
                          <p className="text-xs text-white/70 mt-0.5">{t(activity.actionKey)}</p>
                          <p className="text-xs text-white/60 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {t('security.time.ago')} {activity.time} {t('security.time.min')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </FadeIn>
            {/* Battery Status */}
            <FadeIn delay={0.3}>
              <GlassCard>
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <div className="text-sm font-semibold tracking-tight">{t('security.battery.title')}</div>
                    <div className="mt-1 text-xs text-white/65">
                      {t('security.battery.desc')}
                    </div>
                  </div>
                  <div className="grid size-10 place-items-center rounded-xl bg-white/10 border border-white/10">
                    <Battery className="size-5 text-nexus-blue" />
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { guardKey: 'security.guard.alpha', battery: 85, status: 'good' },
                    { guardKey: 'security.guard.beta', battery: 92, status: 'good' },
                    { guardKey: 'security.guard.delta', battery: 45, status: 'warning' },
                  ].map((guard, index) => (
                    <div key={index} className="rounded-xl border border-white/10 bg-white/6 px-3 py-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm text-white/90">{t(guard.guardKey)}</span>
                        <span
                          className={`text-xs font-medium ${
                            guard.battery > 50
                              ? 'text-green-400'
                              : guard.battery > 20
                                ? 'text-yellow-400'
                                : 'text-red-400'
                          }`}
                        >
                          {guard.battery}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            guard.battery > 50
                              ? 'bg-gradient-to-r from-green-500 to-green-400'
                              : guard.battery > 20
                                ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                                : 'bg-gradient-to-r from-red-500 to-red-400'
                          }`}
                          style={{ width: `${guard.battery}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </FadeIn>
          </div>
          
        </div>
      </div>
    </section>
  )
}
